import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import Icon from '../docs/Icon'
import ParticleField, { type ParticleTarget } from './ParticleField'
import { AssembleScene, FlagScene, ManifestStream, VerifyScene } from './AgentScenes'
import { usePrefs } from '@/state/prefs'
import { useReducedMotion } from '@/state/useReducedMotion'
import Cased from '../ui/Cased'
import { useCustomRoles } from '@/state/useCustomRoles'

const targets: ParticleTarget[] = [{ kind: 'text', text: 'MDS' }, { kind: 'scatter' }]

const steps = [
  {
    title: 'Built for Super Intelligence.',
    text: 'America is racing to lead in Super Intelligence. More government software will be written by agents, and they need the same design system a person sees.',
  },
  {
    title: 'Load the design files.',
    text: 'Tokens, themes, components, patterns and rules ship as plain JSON and CSS at stable URLs under /mds/, listed in one manifest.',
  },
  {
    title: 'Apply the mission.',
    text: 'One theme file per agency, verified for contrast in light and dark. Set data-mission and the whole interface follows.',
  },
  {
    title: 'Compose the interface.',
    text: 'Agents choose from the same components and patterns, with anatomy, accessibility notes and acceptance checks for each.',
  },
  {
    title: 'Verify the work.',
    text: 'Every rule names its check: types, lint, axe on every route, keyboard paths and 320 pixel reflow.',
  },
]

/** Scroll position through the pinned section, as a step index. */
function useStep(section: React.RefObject<HTMLElement | null>, count: number, enabled: boolean) {
  const [step, setStep] = useState(0)
  useEffect(() => {
    const el = section.current
    if (!el || !enabled) return
    let frame = 0
    const update = () => {
      frame = 0
      const rect = el.getBoundingClientRect()
      const travel = el.offsetHeight - window.innerHeight
      const progress = Math.min(0.999, Math.max(0, -rect.top / Math.max(1, travel)))
      setStep(Math.floor(progress * count))
    }
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update)
    }
    update()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
      cancelAnimationFrame(frame)
    }
  }, [section, count, enabled])
  return step
}

/**
 * The agents section: a field of particles in the mission's colors that forms
 * MDS, then a manifest, the flag, components and a check as the visitor
 * scrolls through how an agent builds with the system.
 */
export default function AgentsField() {
  const { prefs } = usePrefs()
  const customRoles = useCustomRoles()
  const reduced = useReducedMotion()
  const section = useRef<HTMLElement>(null)
  const [wide, setWide] = useState(false)
  useEffect(() => {
    const query = window.matchMedia('(min-width: 62rem)')
    const update = () => setWide(query.matches)
    update()
    query.addEventListener('change', update)
    return () => query.removeEventListener('change', update)
  }, [])
  const pinned = wide && !reduced
  const step = useStep(section, steps.length, pinned)
  // The list keeps the height it has with its longest description open, so
  // the button under it stays in one place whichever step is open.
  const list = useRef<HTMLOListElement>(null)
  useLayoutEffect(() => {
    const element = list.current
    if (!element) return
    const fit = () => {
      element.style.minHeight = ''
      if (!pinned) return
      const texts = [...element.querySelectorAll<HTMLElement>('li > p')]
      const open = texts.find((text) => text.parentElement?.classList.contains('is-current'))
      if (!open) return
      const tallest = Math.max(...texts.map((text) => text.scrollHeight))
      element.style.minHeight = `${element.offsetHeight - open.offsetHeight + tallest}px`
    }
    fit()
    window.addEventListener('resize', fit)
    return () => window.removeEventListener('resize', fit)
  }, [pinned, step])
  // Each step owns an equal slice of the pinned scroll; a heading jumps to the
  // middle of its slice.
  const goTo = (index: number) => {
    const el = section.current
    if (!el) return
    const travel = el.offsetHeight - window.innerHeight
    const top = el.getBoundingClientRect().top + window.scrollY
    window.scrollTo({ top: top + (travel * (index + 0.5)) / steps.length, behavior: 'smooth' })
  }
  const memoTargets = useMemo(() => targets, [])

  return (
    <section
      ref={section}
      className={`home-agents${pinned ? ' home-agents--pinned' : ''}`}
      data-theme="dark"
      data-mission={prefs.mission}
      style={{ ...customRoles('dark'), ...(pinned ? { height: `${steps.length * 70 + 60}vh` } : {}) }}
      aria-labelledby="agents-title"
    >
      <div className="home-agents__stage">
        <ParticleField
          targets={memoTargets}
          index={pinned && step > 0 ? 1 : 0}
          still={reduced}
          areaStart={wide ? 0.42 : 0}
          areaWidth={wide ? 0.56 : 1}
          className="home-agents__field"
        />
        {wide && (
          <div className="home-agents__scenes" data-step={pinned ? step : 0}>
            <div className="home-agents__scene" data-scene="1">
              <ManifestStream active={pinned && step === 1} still={reduced} />
            </div>
            <div className="home-agents__scene" data-scene="2">
              <FlagScene active={pinned && step === 2} still={reduced} />
            </div>
            <div className="home-agents__scene" data-scene="3">
              <AssembleScene active={(pinned && step === 3) || reduced} />
            </div>
            <div className="home-agents__scene" data-scene="4">
              <VerifyScene active={(pinned && step === 4) || reduced} />
            </div>
          </div>
        )}
        <div className="mds-container home-agents__content">
          <div className="home-agents__copy">
            <span className="mds-eyebrow">Agents</span>
            <h2 id="agents-title" className="mds-display">
              <Cased>Mission first. Human centered. Agent ready.</Cased>
            </h2>
            <ol ref={list} className="home-agents__steps">
              {steps.map((item, index) => (
                <li key={item.title} className={!pinned || index === step ? 'is-current' : undefined} aria-current={pinned && index === step ? 'step' : undefined}>
                  <h3 className="mds-h3">
                    {pinned ? (
                      <button
                        type="button"
                        className={`home-agents__jump${index < step ? ' is-before' : ''}`}
                        onClick={() => goTo(index)}
                      >
                        <Cased>{item.title}</Cased>
                        {index !== step && <Icon name="chevron" size={16} />}
                      </button>
                    ) : (
                      <Cased>{item.title}</Cased>
                    )}
                  </h3>
                  <p>{item.text}</p>
                </li>
              ))}
            </ol>
            <p className="mds-cluster">
              <Link to="/agents" className="mds-button mds-button--primary">
                Meet your agent toolkit <Icon name="arrow" size={16} />
              </Link>
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}
